/**
 * Real DemoMockProvider fetch interceptor.
 *
 * Replaces the previous placeholder pass-through with a routing layer that
 * intercepts requests to known API paths and returns deterministic fixture
 * responses. Unknown paths fall through to the real `fetch`.
 *
 * Design goals:
 *   - Deterministic output (same params → same response; no randomness).
 *   - Realistic latency: a 200ms "thinking" delay, then token-by-token stream.
 *   - Non-invasive: easy to install/uninstall so we never break prod UX.
 *   - Type-safe and framework-agnostic (plain TS, no React deps in this file).
 */

import { ALL_FIXTURES, FixtureResponder, streamChunksToSSE, ChatFixture, journeyFixtures } from './fixtures';
import { integrations, memoryTimeline, marketingCampaigns } from './fixtures/montage';
import { documentFixtures } from './fixtures/document-reader';

const THINKING_DELAY_MS = 200;

function jsonResponse(body: unknown, status = 200, extraHeaders: Record<string, string> = {}): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      ...extraHeaders,
    },
  });
}

async function thinkingDelay(): Promise<void> {
  return new Promise((r) => setTimeout(r, THINKING_DELAY_MS));
}

function streamResponse(fixture: ChatFixture, status = 200): Response {
  return new Response(streamChunksToSSE(fixture.chunks), {
    status,
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      Connection: 'keep-alive',
      'X-Moonshot-Fixture': fixture.id,
    },
  });
}

/**
 * Resolve the active journey fixture. Priority:
 *   1. Explicit `journey` query param on the request URL.
 *   2. Global moonshot state from `window.location.search`.
 *   3. Default to journey 1.
 */
function resolveFixture(url: URL): ChatFixture {
  const q = url.searchParams.get('journey');
  if (q) {
    const n = parseInt(q, 10);
    if (journeyFixtures[n]) return journeyFixtures[n];
  }
  if (typeof window !== 'undefined') {
    const wp = new URLSearchParams(window.location.search);
    const wj = parseInt(wp.get('journey') || '1', 10);
    if (journeyFixtures[wj]) return journeyFixtures[wj];
  }
  return journeyFixtures[1];
}

/**
 * Static matcher helpers.
 */
function pathMatchesAny(pathname: string, patterns: RegExp[]): boolean {
  return patterns.some((p) => p.test(pathname));
}

const CHAT_PATTERNS = [
  /\/api\/chat(?:\/|$)/i,
  /\/api\/messages(?:\/|$)/i,
  /\/chat\/stream(?:\/|$)/i,
  /\/prest0n\/chat(?:\/|$)/i,
];

const RESEARCH_PATTERNS = [
  /\/api\/(?:deep[-_]?research|research)(?:\/|$)/i,
  /\/research\/run(?:\/|$)/i,
];

const DOC_PATTERNS = [
  /\/api\/(?:documents|files|upload|intake)(?:\/|$)/i,
  /\/doc(?:ument)?s?\/process(?:\/|$)/i,
];

const TRANSLATE_PATTERNS = [
  /\/api\/translate(?:\/|$)/i,
  /\/translate\/(?:es|en)(?:\/|$)/i,
];

const MEMORY_PATTERNS = [/\/api\/memory(?:\/|$)/i, /\/memory\/timeline(?:\/|$)/i];
const INTEGRATION_PATTERNS = [/\/api\/integrations?(?:\/|$)/i];
const MARKETING_PATTERNS = [/\/api\/(?:marketing|campaigns?)(?:\/|$)/i];

/**
 * Responder for the main chat streaming endpoint. Selects the fixture based
 * on the active journey and streams SSE chunks.
 */
const chatResponder: FixtureResponder = {
  match: (url, method) =>
    (method === 'POST' || method === 'GET') && pathMatchesAny(url.pathname, CHAT_PATTERNS),
  respond: async (_req, url) => {
    await thinkingDelay();
    const fixture = resolveFixture(url);
    return streamResponse(fixture);
  },
};

const researchResponder: FixtureResponder = {
  match: (url, method) =>
    (method === 'POST' || method === 'GET') && pathMatchesAny(url.pathname, RESEARCH_PATTERNS),
  respond: async () => {
    await thinkingDelay();
    return streamResponse(journeyFixtures[2]);
  },
};

const documentResponder: FixtureResponder = {
  match: (url, method) =>
    (method === 'POST' || method === 'GET') && pathMatchesAny(url.pathname, DOC_PATTERNS),
  respond: async (_req, url) => {
    if (url.searchParams.get('format') === 'manifest' || url.pathname.endsWith('/manifest')) {
      await thinkingDelay();
      return jsonResponse({
        documents: documentFixtures.map((d, i) => ({
          index: i + 1,
          filename: d.filename,
          wordCount: d.words,
          tags: d.tags,
          summary: d.summary,
        })),
      });
    }
    await thinkingDelay();
    return streamResponse(journeyFixtures[3]);
  },
};

const translateResponder: FixtureResponder = {
  match: (url, method) => method === 'POST' && pathMatchesAny(url.pathname, TRANSLATE_PATTERNS),
  respond: async () => {
    await thinkingDelay();
    return streamResponse(journeyFixtures[5]);
  },
};

const memoryResponder: FixtureResponder = {
  match: (url, method) => method === 'GET' && pathMatchesAny(url.pathname, MEMORY_PATTERNS),
  respond: async () => jsonResponse({ items: memoryTimeline }),
};

const integrationsResponder: FixtureResponder = {
  match: (url, method) => method === 'GET' && pathMatchesAny(url.pathname, INTEGRATION_PATTERNS),
  respond: async () => jsonResponse({ items: integrations }),
};

const marketingResponder: FixtureResponder = {
  match: (url, method) => method === 'GET' && pathMatchesAny(url.pathname, MARKETING_PATTERNS),
  respond: async () => jsonResponse({ items: marketingCampaigns }),
};

/**
 * Hidden fixture introspection endpoint — lets the moonshot UI pull a full
 * fixture list without hammering real APIs.
 */
const fixtureListResponder: FixtureResponder = {
  match: (url, method) => method === 'GET' && /\/moonshot\/fixtures(?:\/|$)/.test(url.pathname),
  respond: async () =>
    jsonResponse({
      fixtures: ALL_FIXTURES.map((f) => ({
        id: f.id,
        userQuery: f.userQuery,
        steps: f.steps,
        chunkCount: f.chunks.length,
      })),
    }),
};

const RESPONDERS: FixtureResponder[] = [
  chatResponder,
  researchResponder,
  documentResponder,
  translateResponder,
  memoryResponder,
  integrationsResponder,
  marketingResponder,
  fixtureListResponder,
];

/**
 * Core mock fetch: resolve URL, try each responder, fall through to real fetch.
 */
export async function mockFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  realFetch: typeof fetch = fetch,
): Promise<Response> {
  let url: URL;
  let method = 'GET';
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    if (typeof input === 'string') {
      url = new URL(input, base);
      method = (init?.method || 'GET').toUpperCase();
    } else if (input instanceof URL) {
      url = input;
      method = (init?.method || 'GET').toUpperCase();
    } else {
      // Request object
      url = new URL((input as Request).url, base);
      method = ((input as Request).method || init?.method || 'GET').toUpperCase();
    }
  } catch {
    return realFetch(input as any, init);
  }

  for (const r of RESPONDERS) {
    try {
      if (r.match(url, method)) {
        // Reconstruct a Request for the responder. Most responders don't use
        // the body, but we pass one anyway for completeness.
        const req =
          input instanceof Request ? input.clone() : new Request(url.toString(), init as RequestInit);
        return await r.respond(req, url);
      }
    } catch (err) {
      // On responder failure, fall through to the next responder, then to real fetch.
       
      console.warn('[moonshot] responder error, falling through:', err);
    }
  }

  // Unknown path → real network. Static assets, fonts, firebase, etc.
  return realFetch(input as any, init);
}

/**
 * Install a global `window.fetch` override. Returns an uninstall function.
 * Safe to call multiple times — it only installs once per realFetch target.
 */
let installed = false;
let originalFetch: typeof fetch | null = null;

export function installGlobalMockFetch(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  if (installed) return uninstallGlobalMockFetch;
  originalFetch = window.fetch.bind(window);
  const wrapped: typeof fetch = (input, init) =>
    mockFetch(input as RequestInfo | URL, init as RequestInit | undefined, originalFetch || fetch);
  (window as any).fetch = wrapped;
  installed = true;
  return uninstallGlobalMockFetch;
}

export function uninstallGlobalMockFetch(): void {
  if (typeof window === 'undefined' || !installed || !originalFetch) return;
  (window as any).fetch = originalFetch;
  installed = false;
  originalFetch = null;
}

export function isMockFetchInstalled(): boolean {
  return installed;
}
